// src/layouts/MemberLayout.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { createPortal } from "react-dom";

import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";
import { MEMBER_PROGRESS, PATHS } from "@/app/constants";
import api from "@/utils/axios";

import cls from "./MemberLayout.module.scss";

import manIcon from "@/assets/UI/man.webp";
import womanIcon from "@/assets/UI/woman.webp";
import non_binaryIcon from "@/assets/UI/non_binary.webp";
import trans_manIcon from "@/assets/UI/trans_man.webp";
import trans_womanIcon from "@/assets/UI/trans_woman.webp";
import genderfluidIcon from "@/assets/UI/genderfluid.webp";
import self_describeIcon from "@/assets/UI/self_describe.webp";
import myProfileIcon from "@/assets/UI/myProfile.webp";

import FooterMobile from "@/components/NotyFooter/FooterMobile";
import FooterDesktop from "@/components/NotyFooter/FooterDesktop";
import CallOverlay from "@/components/Calls/CallOverlay";

import avatarPng from "@/assets/UI/avatar.png";
import homeIcon from "@/assets/UI/home.webp";
import menuIcon from "@/assets/UI/menu.webp";
import whoIcon from "@/assets/UI/who.webp";
import lawIcon from "@/assets/UI/law.webp";
import lanIcon from "@/assets/UI/lan.webp";
import fbIcon from "@/assets/UI/fb.webp";
import instagramIcon from "@/assets/UI/instagram.webp";
import youtubeIcon from "@/assets/UI/youtube.webp";
import tiktokIcon from "@/assets/UI/tiktok.webp";
import logoutIcon from "@/assets/UI/logout.webp";
import notificationsIcon from "@/assets/UI/notifications.webp";
import chatIcon from "@/assets/UI/chat.webp";
import walletIcon from "@/assets/UI/wallet.webp";

const fadeIn = {
  hidden: { opacity: 0, y: -8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
};

const GENDER_ICON_SRC_MAP = {
  man: manIcon,
  woman: womanIcon,
  non_binary: non_binaryIcon,
  trans_man: trans_manIcon,
  trans_woman: trans_womanIcon,
  genderfluid: genderfluidIcon,
  self_describe: self_describeIcon,
  not_select: self_describeIcon,
};

function getGenderIconSrc(gender) {
  return GENDER_ICON_SRC_MAP[gender] || self_describeIcon;
}

const MEMBER_CITIZENSHIP_TYPES = Object.freeze({
  EXPLORER: "explorer_citizen",
  NEXUS: "nexus_citizen",
  LUMORA: "lumora_citizen",
});

function getCitizenshipTheme(citizenshipType) {
  const type = String(citizenshipType || "").toLowerCase();
  if (type === MEMBER_CITIZENSHIP_TYPES.NEXUS) return "nexus";
  if (type === MEMBER_CITIZENSHIP_TYPES.LUMORA) return "lumora";
  return "explorer";
}

function getMemberStatusPath(status) {
  switch (status) {
    case MEMBER_PROGRESS.LOCAL:
      return PATHS.MEMBER_LOCAL;
    case MEMBER_PROGRESS.AVATAR:
      return PATHS.MEMBER_AVATAR;
    case MEMBER_PROGRESS.GENDER:
      return PATHS.MEMBER_GENDER;
    case MEMBER_PROGRESS.RELATION:
      return PATHS.MEMBER_RELATION;
    case MEMBER_PROGRESS.SEXUAL_IDENTITY:
      return PATHS.SEXUAL_IDENTITY;
    case MEMBER_PROGRESS.SEXUAL_ROLE:
      return PATHS.SEXUAL_ROLE;
    case MEMBER_PROGRESS.ATTRACTION_GENDERS:
      return PATHS.ATTRACTION_GENDERS;
    case MEMBER_PROGRESS.CONNECTION_TYPES:
      return PATHS.CONNECTION_TYPES;
    case MEMBER_PROGRESS.INTIMACY_TYPES:
      return PATHS.INTIMACY_TYPES;
    case MEMBER_PROGRESS.SEXUAL_FANTASIES:
      return PATHS.SEXUAL_FANTASIES;
    case MEMBER_PROGRESS.BODY_TYPE:
      return PATHS.BODY_TYPE;
    case MEMBER_PROGRESS.BODY_ATTRACT:
      return PATHS.BODY_ATTRACT;
    case MEMBER_PROGRESS.SEXUAL_ENVIRONMENT:
      return PATHS.SEXUAL_ENVIRONMENT;
    case MEMBER_PROGRESS.EVENT_ATTRACT:
      return PATHS.EVENT_ATTRACT;
    case MEMBER_PROGRESS.ALCOHOL_CANNABIS:
      return PATHS.ALCOHOL_CANNABIS;
    case MEMBER_PROGRESS.BE_MATCHED:
      return PATHS.BE_MATCHED;
    case MEMBER_PROGRESS.JOIN_HOST:
      return PATHS.JOIN_HOST;
    case MEMBER_PROGRESS.REAL_IMAGE:
      return PATHS.REAL_IMAGE;
    case MEMBER_PROGRESS.SOCIAL:
      return PATHS.SOCIAL;
    case MEMBER_PROGRESS.PAYMENT:
      return PATHS.PAYMENT;
    case MEMBER_PROGRESS.PENDING:
      return PATHS.PENDING;
    case MEMBER_PROGRESS.APPROVED:
      return PATHS.APPROVED;
    default:
      return PATHS.MEMBER_CREATE;
  }
}

export default function MemberLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const mainRef = useRef(null);

  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [mlLoading, setMlLoading] = useState(true);
  const [mlData, setMlData] = useState(null);
  const [mlError, setMlError] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setMlLoading(true);
        setMlError(null);
        const { data } = await api.get("/v1/member-layout");
        if (!ignore) setMlData(data || null);
      } catch (err) {
        if (!ignore) setMlError(err);
      } finally {
        if (!ignore) setMlLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  const hasMember = mlData?.hasMember;
  const member = hasMember ? mlData.member : null;
  const mlInitialLoading = mlLoading && !mlData && !mlError;

  const avatarSrc = member?.avatar?.url || avatarPng;
  const rawUsername = member?.username || user?.username || "N";
  const memberType = user?.member_type || member?.memberType || "single";
  const isCouple = memberType === "couple";

  const rawGender = user?.member_gender ?? member?.gender ?? "not_select";
  const memberStatus = member?.status;
  const memberStatusPath = getMemberStatusPath(memberStatus);

  const genderSrc = getGenderIconSrc(rawGender);

  const rawPartnerGender =
    user?.partner_gender ?? member?.partnerGender ?? null;
  const partnerGenderSrc = rawPartnerGender
    ? getGenderIconSrc(rawPartnerGender)
    : null;

  const citizenshipType = user?.citizenshipType ?? member?.citizenshipType;
  const citizenshipTheme = getCitizenshipTheme(citizenshipType);

  const unreadNotificationsCount = mlData?.unreadNotificationsCount ?? 0;
  const unreadChatCount = mlData?.unreadChatCount ?? 0;

  useEffect(() => {
    if (hash) return;
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);

  useEffect(() => {
    if (!menuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 60);

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const handleLogout = useCallback(async () => {
    try {
      await api.post("/v1/auth/logout", {}, { withCredentials: true });
    } catch {
      // ignore logout API failure and clear local auth state anyway
    } finally {
      localStorage.removeItem("cm_at");
      sessionStorage.removeItem("cm_at");
      delete api.defaults?.headers?.common?.Authorization;
      setUser(null);
      window.location.replace("/login");
    }
  }, [setUser]);

  const handleProfileClick = () => {
    if (memberStatus === "pending") {
      navigate("/member/my-profile");
    }
  };

  const QuickIcon = ({
    src,
    alt,
    ring = "pink",
    active = false,
    className = "",
  }) => {
    const ringClass =
      ring === "pink"
        ? cls.ringPink
        : ring === "blue"
          ? cls.ringBlue
          : ring === "purple"
            ? cls.ringPurple
            : ring === "mint"
              ? cls.ringMint
              : ring === "gold"
                ? cls.ringGold
                : ring === "black"
                  ? cls.ringBlack
                  : ring === "red"
                    ? cls.ringNotifRed
                    : cls.ringBlack;

    return (
      <span
        className={`${cls.iconImgWrap} ${ringClass} ${active ? cls.iconActive : ""} ${className}`.trim()}
        aria-hidden="true"
      >
        <img className={cls.iconImg} src={src} alt={alt || ""} />
      </span>
    );
  };

  const hiText = i18n.t("homeLayout.hi");

  const menuSections = useMemo(() => {
    const env = import.meta.env;

    const memberItems = [
      {
        type: "internal",
        to: memberStatusPath,
        label: i18n.t("homeLayout.home"),
        icon: homeIcon,
      },
    ];

    if (memberStatus === "pending") {
      memberItems.push({
        type: "internal",
        to: "/member/my-profile",
        label: i18n.t("homeLayout.myProfile"),
        icon: myProfileIcon,
      });
    }

    memberItems.push(
      {
        type: "internal",
        to: "/member/notifications",
        label: i18n.t("homeLayout.notifications"),
        icon: notificationsIcon,
      },
      {
        type: "internal",
        to: "/member/chat",
        label: i18n.t("homeLayout.chat"),
        icon: chatIcon,
      },
      {
        type: "internal",
        to: "/member/wallet",
        label: i18n.t("homeLayout.wallet"),
        icon: walletIcon,
      },
    );

    return [
      {
        title: "Member Space",
        items: memberItems,
      },
      {
        title: "Profile Progress",
        items: [
          {
            type: "internal",
            to: memberStatusPath,
            label: "Continue setup",
            icon: homeIcon,
          },
          {
            type: "internal",
            to: "/member/my-profile",
            label: i18n.t("homeLayout.myProfile"),
            icon: myProfileIcon,
            hidden: memberStatus !== "pending",
          },
          {
            type: "internal",
            to: "/member/wallet",
            label: i18n.t("homeLayout.wallet"),
            icon: walletIcon,
          },
        ].filter((item) => !item.hidden),
      },
      {
        title: "Universe Info",
        items: [
          {
            type: "external-internal",
            to: "/language",
            label: i18n.t("homeLayout.language"),
            icon: lanIcon,
          },
          {
            type: "external-internal",
            to: "/about-us",
            label: i18n.t("homeLayout.aboutUs"),
            icon: whoIcon,
          },
          {
            type: "external-internal",
            to: "/terms",
            label: i18n.t("homeLayout.terms"),
            icon: lawIcon,
          },
        ],
      },
      {
        title: "Social & Account",
        items: [
          {
            type: "external",
            href: env.VITE_SOCIAL_FACEBOOK,
            label: i18n.t("homeLayout.facebook"),
            icon: fbIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_INSTAGRAM,
            label: i18n.t("homeLayout.instagram"),
            icon: instagramIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_YOUTUBE,
            label: i18n.t("homeLayout.youtube"),
            icon: youtubeIcon,
          },
          {
            type: "external",
            href: env.VITE_SOCIAL_TIKTOK,
            label: i18n.t("homeLayout.tiktok"),
            icon: tiktokIcon,
          },
          { type: "divider" },
          {
            type: "action",
            onClick: handleLogout,
            label: i18n.t("homeLayout.logOut"),
            icon: logoutIcon,
            danger: true,
          },
        ],
      },
    ];
  }, [memberStatus, memberStatusPath, handleLogout]);

  const MenuItem = ({ item }) => {
    if (item.type === "divider") {
      return <li className={cls.menuDivider} aria-hidden="true" />;
    }

    const commonClass = `${cls.menuCard} ${item.danger ? cls.menuCardDanger : ""}`;

    if (item.type === "internal") {
      return (
        <li>
          <Link
            to={item.to}
            className={commonClass}
            onClick={() => setMenuOpen(false)}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ›
            </span>
          </Link>
        </li>
      );
    }

    if (item.type === "external-internal") {
      return (
        <li>
          <Link
            to={item.to}
            target="_blank"
            rel="noopener noreferrer"
            className={commonClass}
            onClick={() => setMenuOpen(false)}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ↗
            </span>
          </Link>
        </li>
      );
    }

    if (item.type === "external") {
      return (
        <li>
          <a
            href={item.href}
            target="_blank"
            rel="noreferrer"
            className={commonClass}
            onClick={() => setMenuOpen(false)}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ↗
            </span>
          </a>
        </li>
      );
    }

    if (item.type === "action") {
      return (
        <li>
          <button
            type="button"
            className={commonClass}
            onClick={() => {
              setMenuOpen(false);
              item.onClick?.();
            }}
          >
            <img
              className={cls.menuCardIcon}
              src={item.icon}
              alt=""
              loading="lazy"
            />
            <span className={cls.menuCardLabel}>{item.label}</span>
            <span className={cls.menuCardChevron} aria-hidden="true">
              ›
            </span>
          </button>
        </li>
      );
    }

    return null;
  };

  return (
    <>
      <main className={cls.home}>
        <section className={`nu-shell ${cls.shell}`}>
          <div className={cls.stage}>
            <div className={cls.topLeftBar}>
              <Motion.button
                type="button"
                className={`${cls.iconBtn} ${cls.profileBtn} ${
                  cls[`profileBtn--${citizenshipTheme}`]
                }`}
                variants={fadeIn}
                initial="hidden"
                animate="show"
                aria-label="Open profile"
                onClick={handleProfileClick}
                disabled={memberStatus !== "pending"}
                aria-disabled={memberStatus !== "pending"}
              >
                <span
                  className={`${cls.avatarShell} ${
                    isCouple ? cls["avatarShell--couple"] : ""
                  } ${cls[`avatarShell--${citizenshipTheme}`]}`}
                >
                  <span className="avatar-wrap">
                    <img src={avatarSrc} alt="" className="avatar avatar-sm" />
                  </span>

                  {mlInitialLoading && (
                    <span className={cls.avatarSpinner} aria-hidden="true" />
                  )}

                  {isCouple ? (
                    <span
                      className={`${cls.genderBadge} ${cls.genderBadgePair}`}
                      aria-hidden="true"
                      title="Couple"
                    >
                      <span className={cls.genderBadgeItem}>
                        <img
                          className={cls.genderBadgeImg}
                          src={genderSrc}
                          alt=""
                          draggable="false"
                        />
                      </span>

                      {partnerGenderSrc && (
                        <span className={cls.genderBadgeItem}>
                          <img
                            className={cls.genderBadgeImg}
                            src={partnerGenderSrc}
                            alt=""
                            draggable="false"
                          />
                        </span>
                      )}
                    </span>
                  ) : (
                    <span
                      className={cls.genderBadge}
                      aria-hidden="true"
                      title="Gender"
                    >
                      <img
                        className={cls.genderBadgeImg}
                        src={genderSrc}
                        alt=""
                        draggable="false"
                      />
                    </span>
                  )}
                </span>

                <span className={cls.profileHi}>
                  {mlInitialLoading ? <>{hiText}, …</> : <>{rawUsername}</>}
                </span>

                {mlError && !mlInitialLoading && (
                  <span className={cls.profileError}>
                    ⚠️ Can't refresh profile
                  </span>
                )}
              </Motion.button>

              <div className={cls.topCenterActions}>
                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label={i18n.t("homeLayout.notifications")}
                  onClick={() => navigate("/member/notifications")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon
                      src={notificationsIcon}
                      alt={i18n.t("homeLayout.notifications")}
                      ring={unreadNotificationsCount > 0 ? "red" : "black"}
                    />

                    {unreadNotificationsCount > 0 && (
                      <span className={cls.notificationBadge}>
                        {unreadNotificationsCount > 99
                          ? "99+"
                          : unreadNotificationsCount}
                      </span>
                    )}
                  </span>
                </Motion.button>

                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label="Wallet"
                  onClick={() => navigate("/member/wallet")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon src={walletIcon} alt="Wallet" ring="black" />
                  </span>
                </Motion.button>

                <Motion.button
                  type="button"
                  className={`${cls.iconBtn} ${cls.notifBtn}`}
                  variants={fadeIn}
                  initial="hidden"
                  animate="show"
                  aria-label={i18n.t("homeLayout.chat")}
                  onClick={() => navigate("/member/chat")}
                >
                  <span className={cls.notificationWrap}>
                    <QuickIcon
                      src={chatIcon}
                      alt={i18n.t("homeLayout.chat")}
                      ring={unreadChatCount > 0 ? "red" : "black"}
                    />

                    {unreadChatCount > 0 && (
                      <span className={cls.notificationBadge}>
                        {unreadChatCount > 99 ? "99+" : unreadChatCount}
                      </span>
                    )}
                  </span>
                </Motion.button>
              </div>
            </div>

            <Motion.button
              type="button"
              className={`${cls.iconBtn} ${cls.menuToggle}`}
              variants={fadeIn}
              initial="hidden"
              animate="show"
              aria-expanded={menuOpen ? "true" : "false"}
              aria-controls="home-fullmenu"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Open menu"
            />

            {createPortal(
              <span className={cls.menuIconOverlay} aria-hidden="true">
                <span className={`${cls.iconImgWrap} ${cls.ringPurple}`}>
                  <img
                    src={menuIcon}
                    alt={i18n.t("loungeLayout.menu")}
                    className={cls.iconImg}
                  />
                </span>
              </span>,
              document.body,
            )}

            <div className={cls.main} ref={mainRef}>
              <Outlet
                context={{
                  memberType,
                  isCouple,
                  rawGender,
                  rawPartnerGender,
                  avatarSrc,
                  rawUsername,
                }}
              />
            </div>

            <nav className={cls.quickBar} aria-label="Quick actions">
              <ul className={cls.quickList}>
                <li>
                  <Link
                    className={cls.roundBtn}
                    to={memberStatusPath}
                    aria-label={i18n.t("homeLayout.home")}
                  >
                    <QuickIcon
                      src={homeIcon}
                      alt={i18n.t("homeLayout.home")}
                      ring="pink"
                      active={pathname === memberStatusPath}
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>

                {memberStatus === "pending" && (
                  <li>
                    <Link
                      className={cls.roundBtn}
                      to="/member/my-profile"
                      aria-label={i18n.t("homeLayout.myProfile")}
                    >
                      <QuickIcon
                        src={myProfileIcon}
                        alt={i18n.t("homeLayout.myProfile")}
                        ring="mint"
                        active={pathname.startsWith("/member/my-profile")}
                        className={cls.quickIconUniform}
                      />
                    </Link>
                  </li>
                )}

                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/member/wallet"
                    aria-label={i18n.t("homeLayout.wallet")}
                  >
                    <QuickIcon
                      src={walletIcon}
                      alt={i18n.t("homeLayout.wallet")}
                      ring="blue"
                      active={pathname.startsWith("/member/wallet")}
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>

                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/about-us"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={i18n.t("homeLayout.aboutUs")}
                  >
                    <QuickIcon
                      src={whoIcon}
                      alt={i18n.t("homeLayout.aboutUs")}
                      ring="blue"
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>

                <li>
                  <Link
                    className={cls.roundBtn}
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={i18n.t("homeLayout.terms")}
                  >
                    <QuickIcon
                      src={lawIcon}
                      alt={i18n.t("homeLayout.terms")}
                      ring="purple"
                      className={cls.quickIconUniform}
                    />
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <div
          id="home-fullmenu"
          className={`${cls.fullmenu} ${menuOpen ? cls["is-open"] : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Main Menu"
        >
          <div
            className={cls.fullmenuBackdrop}
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />

          <div className={cls.fullmenuPanel} role="document">
            <button
              ref={closeBtnRef}
              type="button"
              className={`${cls.iconBtn} ${cls.menuClose}`}
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <img
                src={menuIcon}
                alt={i18n.t("loungeLayout.menu")}
                className={cls.iconImg}
              />
            </button>

            <div className={cls.fullmenuInner}>
              <div className={cls.menuSectionGrid}>
                {menuSections.map((section) => (
                  <section className={cls.menuSection} key={section.title}>
                    <h3 className={cls.menuSectionTitle}>{section.title}</h3>

                    <ul className={cls.menuCardList}>
                      {section.items.map((item, idx) => (
                        <MenuItem key={`${section.title}-${idx}`} item={item} />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <CallOverlay />
      <ScrollRestoration getKey={(loc) => loc.pathname} />
      {/* <FooterMobile /> */}
      <FooterDesktop />
    </>
  );
}
